import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Eye, Download, RefreshCw, Trash2, Loader2, Plus, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

const fullUrl = (url) => (url.startsWith('http') ? url : `${BACKEND}${url}`);
const download = (doc) => {
  const a = document.createElement('a');
  a.href = fullUrl(doc.url);
  a.download = doc.filename || 'justificante';
  a.target = '_blank';
  document.body.appendChild(a); a.click(); a.remove();
};
const validFiles = (files) => {
  const list = Array.from(files || []);
  const bad = list.find((f) => !ACCEPTED.includes(f.type));
  if (bad) { toast.error(`"${bad.name}": solo se permiten PDF, JPG o PNG`); return null; }
  return list;
};

function DocRow({ record, doc, index, onUpdated }) {
  const replaceRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const isPdf = doc.content_type === 'application/pdf' || /\.pdf$/i.test(doc.filename || '');

  const replace = async (file) => {
    const list = validFiles(file ? [file] : []);
    if (!list?.length) return;
    const fd = new FormData(); fd.append('file', list[0]);
    setBusy(true);
    try {
      await axios.put(`${API}/daily-cash/${record.id}/documents/${doc.id}`, fd);
      toast.success('Archivo sustituido'); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al sustituir'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar "${doc.filename}"?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/daily-cash/${record.id}/documents/${doc.id}`);
      toast.success('Archivo eliminado'); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`cash-doc-item-${record.id}-${index}`}>
      <input ref={replaceRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
        onChange={(e) => { replace(e.target.files?.[0]); e.target.value = ''; }} data-testid={`replace-cash-doc-input-${record.id}-${index}`} />
      {isPdf ? <FileText size={14} className="text-red-500 shrink-0" /> : <ImageIcon size={14} className="text-blue-500 shrink-0" />}
      <span className="text-xs truncate flex-1" title={doc.filename}>{doc.filename}</span>
      {busy ? <Loader2 size={14} className="animate-spin text-slate-400" /> : (
        <div className="flex items-center gap-0.5 shrink-0">
          <Button size="icon" variant="ghost" className="h-6 w-6 text-violet-600" title="Ver / abrir" onClick={() => window.open(fullUrl(doc.url), '_blank')} data-testid={`view-cash-doc-${record.id}-${index}`}><Eye size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500" title="Descargar" onClick={() => download(doc)} data-testid={`download-cash-doc-${record.id}-${index}`}><Download size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-500" title="Sustituir" onClick={() => replaceRef.current?.click()} data-testid={`replace-cash-doc-${record.id}-${index}`}><RefreshCw size={13} /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:bg-red-50" title="Eliminar" onClick={remove} data-testid={`delete-cash-doc-${record.id}-${index}`}><Trash2 size={13} /></Button>
        </div>
      )}
    </div>
  );
}

export default function CashDoc({ record, onUpdated }) {
  const addRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const docs = record.receipt_docs || (record.receipt_doc ? [record.receipt_doc] : []);

  const upload = async (files) => {
    const list = validFiles(files);
    if (!list?.length) return;
    const fd = new FormData();
    list.forEach((f) => fd.append('files', f));
    setBusy(true);
    try {
      await axios.post(`${API}/daily-cash/${record.id}/documents`, fd);
      toast.success(list.length === 1 ? 'Justificante guardado' : `${list.length} justificantes guardados`);
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir los justificantes'); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-center" data-testid={`cash-doc-${record.id}`}>
      <input ref={addRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid={`cash-doc-input-${record.id}`} />
      {busy ? <Loader2 size={14} className="animate-spin text-slate-400" /> : docs.length === 0 ? (
        <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-violet-600" title="Adjuntar justificantes (PDF/JPG/PNG)"
          onClick={() => addRef.current?.click()} data-testid={`upload-cash-doc-${record.id}`}>
          <Paperclip size={14} />
        </Button>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-violet-700" title={`${docs.length} archivo(s)`} data-testid={`open-cash-docs-${record.id}`}>
              <Paperclip size={14} />
              <Badge variant="outline" className="bg-violet-100 !text-violet-700 border-violet-200 h-5 px-1.5 text-[11px]">{docs.length}</Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-3" data-testid={`cash-docs-popover-${record.id}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold uppercase text-slate-500">Justificantes ({docs.length})</p>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => addRef.current?.click()} data-testid={`add-cash-doc-${record.id}`}>
                <Plus size={12} /> Añadir
              </Button>
            </div>
            {docs.map((d, i) => <DocRow key={d.id || i} record={record} doc={d} index={i} onUpdated={onUpdated} />)}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
